/* eslint-disable max-lines */
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Text,
    Textarea,
    VStack,
} from '@chakra-ui/react';
import {
    Controller, useFormContext,
} from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { ServiceCheckbox } from '@acp/core-ui';
import { ServiceIdMapper } from '@acp/shared';
import { CancellationReasonSelect } from './cancellation-reason-select';
import { getCancellationRequestSummary } from '../../service';
import { RequestCancellationFormValues } from './form-schema';
import { PropertyAddressCard } from '../property-address-card';

function RequestCancellationScreen({
    propertyId,
    onClose,
    onSubmit,
}: {
    propertyId: number;
    onClose: () => void;
    onSubmit: (vals: RequestCancellationFormValues) => void;
}) {
    const form = useFormContext<RequestCancellationFormValues>();

    const cancellationSummaryQuery = useQuery({
        queryKey: ['property', propertyId, 'cancellation-request-summary'],
        queryFn: () => getCancellationRequestSummary(propertyId),
    });

    const hasPendingCancellationServices = useMemo(() => {
        if (cancellationSummaryQuery.data === undefined) return false;

        return cancellationSummaryQuery.data.cancellableServices.some(
            (service) => service.isPendingCancellation
        );
    }, [cancellationSummaryQuery.data]);

    const hasEligibleCancellationServices = useMemo(() => {
        if (cancellationSummaryQuery.data === undefined) return false;

        return cancellationSummaryQuery.data.cancellableServices.some(
            (service) => !service.isPendingCancellation
        );
    }, [cancellationSummaryQuery.data]);

    useEffect(() => {
        if (cancellationSummaryQuery.data === undefined) return;

        const eligibleServices = cancellationSummaryQuery.data.cancellableServices.filter(
            (service) => !service.isPendingCancellation
        );

        // If there's only one eligible service,
        // we can select it by default
        if (eligibleServices.length === 1) {
            form.setValue('services', [eligibleServices[0].id]);
        }

        // If the plan does not allow selecting services,
        // select all services by default
        if (!cancellationSummaryQuery.data.canSelectServices) {
            form.setValue('services', eligibleServices.map((x) => x.id));
        }
    }, [cancellationSummaryQuery.data]);

    return (
        <>
            <ModalHeader>Request service cancellation</ModalHeader>
            <ModalBody>
                <PropertyAddressCard w="full" mb={5} propertyId={propertyId} />
                {
                    cancellationSummaryQuery.isLoading && (
                        <Flex
                            alignItems="center"
                            fontWeight="bold"
                            justifyContent="center"
                            py={8}
                            px={8}
                            borderColor="muted.50"
                            borderWidth={1}
                            rounded="md"
                        >
                            <Spinner />
                        </Flex>
                    )
                }
                {
                    cancellationSummaryQuery.data !== undefined
                    && cancellationSummaryQuery.data.cancellableServices.length === 0 && (
                        <Flex
                            alignItems="center"
                            fontWeight="bold"
                            justifyContent="center"
                            py={8}
                            px={8}
                            borderColor="muted.50"
                            borderWidth={1}
                            rounded="md"
                            color="red.500"
                        >
                            <Text>This property is already cancelled</Text>
                        </Flex>
                    )
                }
                {
                    cancellationSummaryQuery.data !== undefined
                    && cancellationSummaryQuery.data.cancellableServices.length !== 0
                    && !hasEligibleCancellationServices
                    && hasPendingCancellationServices
                    && (
                        <FormControl mb={2}>
                            <FormLabel htmlFor="none">
                                Services eligible to cancel
                            </FormLabel>
                            <Flex
                                flexDir="column"
                                alignItems="center"
                                justifyContent="center"
                                py={8}
                                px={8}
                                borderColor="muted.50"
                                borderWidth={1}
                                rounded="md"
                            >
                                <Text color="red.500" fontWeight="bold">No services available to cancel</Text>
                                <Text fontSize="sm" color="black">All of this properties services already have a pending cancellation request against them</Text>
                            </Flex>
                        </FormControl>
                    )
                }
                {
                    cancellationSummaryQuery.data !== undefined
                    && cancellationSummaryQuery.data.cancellableServices.length !== 0
                    && hasEligibleCancellationServices
                    && (
                        <Controller
                            control={form.control}
                            name="services"
                            render={({ field, fieldState }) => (
                                <FormControl
                                    isInvalid={fieldState.invalid}
                                    isRequired={cancellationSummaryQuery.data.canSelectServices}
                                    mb={2}
                                >
                                    <FormLabel htmlFor="none">
                                        Services eligible to cancel
                                    </FormLabel>

                                    <HStack gap={2} w="full" flexWrap="wrap">
                                        {
                                            cancellationSummaryQuery.data
                                                .cancellableServices
                                                .filter((x) => !x.isPendingCancellation)
                                                .map((service) => (
                                                    <ServiceCheckbox
                                                        key={service.id}
                                                        serviceName={
                                                            ServiceIdMapper.getFormattedServiceName(
                                                                service.shortName
                                                            )
                                                        }
                                                        onChange={(isToBeCancelled) => {
                                                            if (!cancellationSummaryQuery
                                                                .data?.canSelectServices) return;

                                                            const newValue = isToBeCancelled
                                                                ? [...field.value, service.id]
                                                                : field.value.filter(
                                                                    (v) => v !== service.id
                                                                );

                                                            field.onChange(newValue);
                                                        }}
                                                        isChecked={field.value.includes(service.id)}
                                                        hideCheckbox={!cancellationSummaryQuery
                                                            .data?.canSelectServices}
                                                    />
                                                ))
                                        }
                                    </HStack>
                                    {
                                        !cancellationSummaryQuery.data.canSelectServices
                                        && (
                                            <FormHelperText mb={4}>
                                                The plan that this property is enrolled into
                                                does not allow individual service cancellation.
                                                All services will be cancelled.
                                            </FormHelperText>
                                        )
                                    }
                                    {fieldState.error && (
                                        <FormErrorMessage>
                                            {fieldState.error.message}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            )}
                        />
                    )
                }
                {
                    cancellationSummaryQuery.data !== undefined
                    && cancellationSummaryQuery.data.cancellableServices.length !== 0
                    && hasPendingCancellationServices
                    && (
                        <FormControl mb={2}>
                            <FormLabel htmlFor="none">
                                Others
                            </FormLabel>
                            <FormHelperText mb={4}>
                                These services already have a pending cancellation request
                            </FormHelperText>
                            <HStack gap={2} w="full" flexWrap="wrap">
                                {
                                    cancellationSummaryQuery.data
                                        .cancellableServices
                                        .filter((x) => x.isPendingCancellation)
                                        .map((service) => (
                                            <ServiceCheckbox
                                                key={service.id}
                                                serviceName={
                                                    ServiceIdMapper.getFormattedServiceName(
                                                        service.shortName
                                                    )
                                                }
                                                onChange={() => {}}
                                                isChecked={false}
                                                labelText="Pending cancellation"
                                            />
                                        ))
                                }
                            </HStack>
                        </FormControl>
                    )
                }

                <VStack gap={6} py={4}>
                    <Controller
                        control={form.control}
                        name="cancellationReasonId"
                        render={({ field, fieldState }) => (
                            <FormControl isInvalid={fieldState.invalid} isRequired>
                                <FormLabel>Cancellation reason</FormLabel>
                                <CancellationReasonSelect
                                    value={field.value}
                                    onChange={(opt) => field.onChange(opt?.value)}
                                />
                                {fieldState.error && (
                                    <FormErrorMessage>
                                        {fieldState.error.message}
                                    </FormErrorMessage>
                                )}
                            </FormControl>
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormControl>
                                <FormLabel>Feedback</FormLabel>
                                <Textarea {...field} />
                                <FormHelperText>
                                    If there is anything that you would like to share,
                                    we would appreciate it. It helps us improve.
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </VStack>
            </ModalBody>

            <ModalFooter justifyContent="space-between">
                <Button
                    colorScheme="muted"
                    variant="ghost"
                    onClick={onClose}
                >
                    Close
                </Button>
                <Button
                    colorScheme="secondary"
                    onClick={form.handleSubmit(onSubmit)}
                    isDisabled={
                        cancellationSummaryQuery.isLoading
                        || cancellationSummaryQuery.data === undefined
                        || cancellationSummaryQuery.data.cancellableServices.length === 0
                        || !hasEligibleCancellationServices
                    }
                >
                    Next
                </Button>
            </ModalFooter>
        </>
    );
}

export { RequestCancellationScreen };
